import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, assetUrl, formatDate, type CourseResource, type Diplomado, type DiplomadoEdition } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import { InscripcionFromDiplomadoEdition } from "@/components/feature/InscripcionModal";

export default function DiplomadoPublicPage() {
  const branding = useBranding();
  const { id } = useParams<{ id: string }>();
  const [diplomado, setDiplomado] = useState<Diplomado | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEdition, setSelectedEdition] = useState<DiplomadoEdition | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getPublicDiplomado(id).then(setDiplomado).catch(() => setDiplomado(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <i className="ri-loader-4-line animate-spin text-3xl text-[var(--color-accent-oil)]"></i>
      </div>
    );
  }

  if (!diplomado) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
        <PublicNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-[var(--color-text-muted)]">Diplomado no encontrado</p>
            <Link to="/" className="btn-primary mt-4 inline-block">Inicio</Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const title = diplomado.detail_title || diplomado.name;
  const image = branding.courseImage(diplomado.image_url);
  const highlights = diplomado.highlights || [];
  const resources = (diplomado.resource_files || []).filter((resource: CourseResource) => resource?.url);
  const editions = diplomado.editions ?? [];

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
      <PageMeta title={`${diplomado.name} | ${branding.site_name}`} description={diplomado.description.slice(0, 160)} path={`/diplomado/${diplomado.id}`} image={image} />
      <PublicNavbar />
      <div className="flex-1 pt-[88px] pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <section className="relative w-full overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] aspect-[16/9]">
            <img src={image} alt={diplomado.name} className="absolute inset-0 w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080f1a]/80 via-[#080f1a]/15 to-transparent" />
            <div className="absolute left-4 sm:left-6 right-4 sm:right-6 bottom-4 sm:bottom-6 flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-accent-oil)] text-[#080f1a]">{diplomado.category}</span>
              {diplomado.modality && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20">{diplomado.modality}</span>}
            </div>
          </section>

          <div className="grid xl:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8 items-start">
            <main className="space-y-6 min-w-0">
              <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 lg:p-8 min-w-0">
                <span className="text-xs font-semibold text-[var(--color-accent-oil)] uppercase tracking-[0.18em]">Detalle del diplomado</span>
                <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold text-[var(--color-text-primary)] mt-3 leading-[1.04] break-words">{title}</h1>
                {title !== diplomado.name && <p className="text-base sm:text-lg font-semibold text-[var(--color-text-secondary)] mt-3">{diplomado.name}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-6">
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-time-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Duración</p>
                    <p className="text-sm sm:text-base font-bold truncate">{diplomado.duration || `${diplomado.hours}h`}</p>
                  </div>
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-hourglass-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Horas</p>
                    <p className="text-sm sm:text-base font-bold">{diplomado.hours}h</p>
                  </div>
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-3 sm:p-4">
                    <i className="ri-computer-line text-[var(--color-accent-oil)]"></i>
                    <p className="text-[11px] text-[var(--color-text-muted)] mt-2">Modalidad</p>
                    <p className="text-sm sm:text-base font-bold truncate">{diplomado.modality || "Por definir"}</p>
                  </div>
                </div>
              </section>

              <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]"><i className="ri-graduation-cap-line"></i></div>
                  <h2 className="text-xl font-bold">Contenido del diplomado</h2>
                </div>
                <p className="text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-line">{diplomado.description}</p>
              </section>

              <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]"><i className="ri-sparkling-line"></i></div>
                  <h2 className="text-xl font-bold">Aspectos importantes</h2>
                </div>
                {highlights.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {highlights.map((item) => (
                      <div key={item} className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 flex gap-3">
                        <i className="ri-check-double-line text-[var(--color-accent-oil)] mt-0.5"></i>
                        <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--color-text-muted)] text-sm">Agrega puntos destacados desde la edición del diplomado para mostrarlos aquí.</p>
                )}
              </section>

              {resources.length > 0 && (
                <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6">
                  <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
                    <div>
                      <h2 className="text-xl font-bold">Recursos descargables</h2>
                      <p className="text-sm text-[var(--color-text-muted)] mt-1">Materiales, guías o archivos complementarios del diplomado.</p>
                    </div>
                    <i className="ri-download-cloud-2-line text-3xl text-[var(--color-accent-oil)]"></i>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {resources.map((resource) => {
                      const href = assetUrl(resource.url) || resource.url;
                      return (
                        <a key={`${resource.name}-${resource.url}`} href={href} download target="_blank" rel="noreferrer" className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 flex items-center justify-between gap-4 hover:border-[var(--color-accent-oil)]/50 transition-colors">
                          <span className="flex items-center gap-3 min-w-0"><i className="ri-file-download-line text-[var(--color-accent-oil)] text-xl"></i><span className="font-semibold text-sm truncate">{resource.name || "Descargar recurso"}</span></span>
                          <i className="ri-arrow-right-line text-[var(--color-text-muted)]"></i>
                        </a>
                      );
                    })}
                  </div>
                </section>
              )}
            </main>

            <aside className="xl:sticky xl:top-24 order-first xl:order-none">
              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 shadow-2xl shadow-black/20">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <span className="text-xs font-semibold text-[var(--color-accent-oil)] uppercase tracking-[0.16em]">Próximas cohortes</span>
                    <h2 className="text-2xl font-bold mt-1">Ediciones disponibles</h2>
                  </div>
                  <div className="w-11 h-11 rounded-2xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]"><i className="ri-calendar-event-line text-xl"></i></div>
                </div>

                {editions.length === 0 ? (
                  <div className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 text-sm text-[var(--color-text-secondary)]">
                    <p className="font-semibold text-[var(--color-text-primary)]">No hay cohortes abiertas por ahora.</p>
                    <p className="mt-2">Contáctanos para recibir información de próximas fechas.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {editions.map((ed) => (
                      <div key={ed.id} className="rounded-2xl bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-[var(--color-text-primary)] leading-snug">{ed.edition_label || formatDate(ed.start_date)}</p>
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">{formatDate(ed.start_date)} - {formatDate(ed.end_date)}</p>
                          </div>
                          <span className="text-sm font-bold text-[var(--color-accent-oil)] whitespace-nowrap">${ed.price.toLocaleString("en-US")}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-[var(--color-text-muted)]">
                          <span className="flex items-center gap-1 min-w-0"><i className="ri-map-pin-line text-[var(--color-accent-oil)]"></i><span className="truncate">{ed.location || "Sede por definir"}</span></span>
                          <span className="flex items-center gap-1"><i className="ri-group-line text-[var(--color-accent-oil)]"></i>{ed.capacity} cupos</span>
                        </div>
                        {ed.instructor && <p className="text-xs text-[var(--color-text-muted)] mt-2">Instructor: {ed.instructor.full_name}</p>}
                        <button onClick={() => setSelectedEdition({ ...ed, diplomado })} className="btn-primary w-full mt-4 cursor-pointer">
                          Inscribirme
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {editions.length === 0 && <a href="/#contacto" className="btn-primary w-full mt-5 text-center block">Solicitar información</a>}
              </div>

              <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-6 mt-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/15 flex items-center justify-center text-[var(--color-accent-oil)]"><i className="ri-user-star-line"></i></div>
                  <h2 className="text-xl font-bold">A quién va dirigido</h2>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{diplomado.target_audience || "Profesionales, técnicos, supervisores y equipos que buscan una formación profunda y estructurada."}</p>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <PublicFooter />
      {selectedEdition && <InscripcionFromDiplomadoEdition edition={selectedEdition} onClose={() => setSelectedEdition(null)} />}
    </div>
  );
}
